import {
  List,
  ListItem,
  Typography,
  Box,
  type TypographyOwnProps,
} from '@mui/material';
import { createElement, ElementType } from 'react';
import TableRenderer from '../../features/ContentRendering/components/tables/TableRenderer';

function getImage(src: string, chapterId: string) {
  return createElement('img', {
    src,
    style: {
      maxWidth: '100%',
      height: 'auto',
    },
    'data-chapter-id': chapterId,
    'data-has-chapter-id': 'true',
  });
}

function getListItem(item: Element) {
  return <span dangerouslySetInnerHTML={{ __html: item.innerHTML }} />;
}

function getList(
  tagName: ElementType,
  marginBottom: number,
  paddingTop: number,
  children: Array<Element>,
  chapterId: string,
) {
  return (
    <List
      component={tagName}
      sx={{
        listStyle: tagName == 'ol' ? 'decimal' : 'disc',
        pl: 4,
        marginBottom,
        paddingTop,
      }}
      disablePadding
      data-chapter-id={chapterId}
      data-has-chapter-id='true'
    >
      {children.map((item) => (
        <ListItem
          key={item.getAttribute('data-tp')}
          component='li'
          sx={{ display: 'list-item' }}
          disablePadding
        >
          <Typography variant='listItem'>{getListItem(item)}</Typography>
        </ListItem>
      ))}
    </List>
  );
}

function getTypography(
  variant: TypographyOwnProps['variant'],
  tagName: ElementType,
  innerHTML: string,
  id: string | null,
  chapterId: string,
) {
  let styles = {};

  if (tagName == 'blockquote') {
    styles = {
      ...styles,
      paddingLeft: { xs: '1.5rem', sm: '1.6875rem', md: '1.875rem' },
      paddingRight: { xs: '3rem', sm: '3.375rem', md: '3.75rem' },
      borderLeft: 2,
      borderColor: 'divider',
    };
  }

  return (
    <Box
      sx={{
        ...(tagName === 'blockquote' && {
          marginY: { xs: '1rem', sm: '1.135rem', md: '1.25rem' },
        }),
      }}
    >
      <Typography
        id={id ?? undefined}
        variant={variant}
        component={tagName}
        data-chapter-id={chapterId}
        data-has-chapter-id='true'
        sx={{ overflowWrap: 'break-word', ...styles }}
        dangerouslySetInnerHTML={{ __html: innerHTML }}
      />
    </Box>
  );
}

interface ArticleContentSectionProps {
  originalElement: Element;
  readonly marginBottom?: number;
  readonly paddingTop?: number;
}

export default function ArticleContentSection({
  originalElement,
  marginBottom = 0,
  paddingTop = 0,
}: ArticleContentSectionProps) {
  let tagName = originalElement.tagName.toLowerCase();
  let variant = originalElement.tagName;
  let id = null;
  const chapterId = originalElement.getAttribute('data-chapter-id') ?? '';

  if (tagName == 'img') {
    const src = originalElement.getAttribute('src');
    return getImage(src ?? '', chapterId);
  }

  if (tagName == 'ol' || tagName == 'ul') {
    return getList(
      tagName as ElementType,
      marginBottom,
      paddingTop,
      Array.from(originalElement.children),
      chapterId,
    );
  }

  if (tagName.toLowerCase() === 'table') {
    return (
      <TableRenderer
        tableElement={originalElement as HTMLTableElement}
        marginBottom={marginBottom}
        paddingTop={paddingTop}
        chapterId={chapterId}
      />
    );
  }

  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'].includes(tagName)) {
    if (tagName == 'p') {
      variant = 'body1';
    } else {
      const level = parseInt(tagName.charAt(1));
      variant = level > 6 ? 'h6' : 'h' + level;
      id = originalElement.getAttribute('id');
    }

    // Element should be rendered as a quote if it contains class 'case and is a paragraph element
    if (tagName == 'p' && originalElement.classList.contains('case')) {
      tagName = 'blockquote';
      variant = 'listItem';
    }

    return getTypography(
      variant as TypographyOwnProps['variant'],
      tagName as ElementType,
      originalElement.innerHTML,
      id,
      chapterId,
    );
  }
  return (
    <Box dangerouslySetInnerHTML={{ __html: originalElement.outerHTML }} />
  );
}
